import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Check, SkipForward } from "lucide-react";

import { StatusPill } from "@/components/Progress";
import { api } from "@/lib/api-client";
import { useLocale } from "@/lib/locale";
import { cn } from "@/lib/utils";
import type { MaintenanceEvent, MaintenanceView } from "@/lib/domain";

const KIND_LABEL: Record<string, Record<"it" | "en" | "es", string>> = {
  annual_check: { it: "Controllo annuale", en: "Annual check", es: "Control anual" },
  top_coat_renewal: {
    it: "Rinnovo top coat",
    en: "Top-coat renewal",
    es: "Renovación del top coat",
  },
  inspection: { it: "Ispezione", en: "Inspection", es: "Inspección" },
  other: { it: "Altro", en: "Other", es: "Otro" },
};

export function MaintenancePanel({
  surfaceId,
  canAct,
  hasCertificate,
}: {
  surfaceId: string;
  canAct: boolean;
  hasCertificate: boolean;
}) {
  const { locale } = useLocale();
  const [view, setView] = useState<MaintenanceView | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await api.maintenance(surfaceId));
    } catch {
      setView(null);
    }
  }, [surfaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(
    event: MaintenanceEvent,
    status: "done" | "skipped",
  ) {
    let notes: string | undefined;
    if (status === "done") {
      notes =
        window.prompt(
          locale === "it" ? "Esito del controllo (facoltativo)" : "Outcome (optional)",
        ) ?? undefined;
    }
    setBusyId(event.id);
    try {
      await api.updateMaintenance(event.id, { status, notes });
      await load();
    } finally {
      setBusyId(null);
    }
  }

  if (!view) return null;

  const open = view.events.filter(
    (e) => e.status === "scheduled" || e.status === "overdue",
  );
  const shown = showAll ? view.events : open.slice(0, 8);

  return (
    <section className="glass-panel-strong mt-6 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-goldbright">
          {locale === "it" ? "Manutenzione 30 anni" : "30-year maintenance"}
        </h2>
        {view.counts.overdue > 0 && (
          <StatusPill
            status="issue"
            label={`${view.counts.overdue} ${
              locale === "it" ? "in ritardo" : "overdue"
            }`}
          />
        )}
      </div>

      {!hasCertificate && (
        <p className="mt-3 text-[0.8rem] text-ice/60">
          {locale === "it"
            ? "Il programma parte con l'emissione del certificato."
            : "The programme starts when the certificate is issued."}
        </p>
      )}

      {hasCertificate && (
        <>
          <div className="mt-3 grid grid-cols-3 gap-3 text-center">
            {[
              {
                label: locale === "it" ? "Visite" : "Visits",
                value: view.counts.total,
                tone: "text-paper",
              },
              {
                label: locale === "it" ? "Eseguite" : "Done",
                value: view.counts.done,
                tone: "text-green",
              },
              {
                label: locale === "it" ? "In ritardo" : "Overdue",
                value: view.counts.overdue,
                tone: view.counts.overdue ? "text-pink" : "text-ice/50",
              },
            ].map((s) => (
              <div key={s.label} className="rounded-lg border border-white/10 py-2">
                <div className={cn("num font-serif text-lg", s.tone)}>
                  {s.value}
                </div>
                <div className="text-[0.58rem] uppercase tracking-[0.16em] text-ice/50">
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {view.next && (
            <div className="mt-4 flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[0.76rem]">
              <CalendarClock className="h-3.5 w-3.5 text-goldbright" />
              <span className="text-ice/70">
                {locale === "it" ? "Prossima visita" : "Next visit"}:
              </span>
              <span className="num text-paper">{view.next.dueAt.slice(0, 10)}</span>
              <span className="text-ice/60">
                · {KIND_LABEL[view.next.kind]?.[locale] ?? view.next.kind}
              </span>
              {view.next.status === "overdue" && (
                <span className="text-pink">
                  · {locale === "it" ? "in ritardo" : "overdue"}
                </span>
              )}
            </div>
          )}

          <div className="mt-4 grid gap-1.5">
            {shown.map((e) => (
              <div
                key={e.id}
                className="flex flex-wrap items-center gap-3 border-b border-white/10 pb-1.5 text-[0.74rem] last:border-0"
              >
                <span className="num text-ice/60">{e.dueAt.slice(0, 10)}</span>
                <span className="flex-1 text-paper/90">
                  {KIND_LABEL[e.kind]?.[locale] ?? e.kind}
                </span>
                <StatusPill
                  status={e.status}
                  label={
                    e.status === "done"
                      ? locale === "it" ? "Eseguita" : "Done"
                      : e.status === "skipped"
                        ? locale === "it" ? "Saltata" : "Skipped"
                        : e.status === "overdue"
                          ? locale === "it" ? "In ritardo" : "Overdue"
                          : locale === "it" ? "Programmata" : "Scheduled"
                  }
                />
                {canAct && (e.status === "scheduled" || e.status === "overdue") && (
                  <>
                    <button
                      type="button"
                      disabled={busyId === e.id}
                      onClick={() => act(e, "done")}
                      className="inline-flex items-center gap-1 rounded-full border border-green/50 bg-green/10 px-2.5 py-1 text-[0.55rem] font-semibold uppercase tracking-[0.1em] text-green disabled:opacity-50"
                    >
                      <Check className="h-3 w-3" />
                      {locale === "it" ? "Eseguita" : "Done"}
                    </button>
                    <button
                      type="button"
                      disabled={busyId === e.id}
                      onClick={() => act(e, "skipped")}
                      className="inline-flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-1 text-[0.55rem] font-semibold uppercase tracking-[0.1em] text-ice/60 disabled:opacity-50"
                    >
                      <SkipForward className="h-3 w-3" />
                      {locale === "it" ? "Salta" : "Skip"}
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="mt-3 text-[0.62rem] uppercase tracking-[0.16em] text-ice/50 hover:text-paper"
          >
            {showAll
              ? locale === "it" ? "Mostra solo le prossime" : "Show upcoming only"
              : locale === "it" ? "Mostra tutto il programma" : "Show full programme"}
          </button>
        </>
      )}
    </section>
  );
}
